"use client";

import React from "react";

import Page from "@/components/ui/page";
import Section from "@/components/ui/section";
import { tinaFieldSafe, useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import { Card, CardContent } from "@/components/ui/card";
import CommunityEditionForm from "@/components/CommunityEditionForm";
import LogosCarousel from "@/components/LogosCarousel";
import { Check } from "lucide-react";

export default function CommunityEdition(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const header = data?.communityEdition?.header;
  const formSection = data?.communityEdition?.formSection;
  const inActionSection = data?.communityEdition?.inActionSection;
  const includedSection = data?.communityEdition?.includedSection;
  const callToAction = data?.communityEdition?.cta;

  return (
    <Page
      defaultTinaField={data?.communityEdition}
      seo={data?.communityEdition?.seo}
      pagePath="/starter-edition"
      titleFallback="Starter Edition"
    >
      <Header
        {...header}
        variant="left"
        backgroundVariant="pixels-l-r"
        wrapperClassName="pb-40 md:pb-32 [&_h1]:lg:!text-5xl [&>div:last-child]:grid [&>div:last-child]:lg:grid-cols-2 [&>div:last-child]:gap-8 [&>div:last-child]:grid-rows-[auto_1fr] [&>div]:lg:!gap-12 [&>div]:lg:!gap-16"
      >
        <Card
          animateOnHover={false}
          wrapperClassName="row-span-1 max-md:order-2 max-sm:py-6"
          backgroundVariant="default"
        >
          <CardContent>
            <CommunityEditionForm formSection={formSection} />
          </CardContent>
        </Card>
      </Header>
      <LogosCarousel />
      {(inActionSection || includedSection) && (
        <Section wrapperClassName="lg:!pb-32" className="space-y-12">
          {inActionSection && (
            <div className="space-y-3">
              <h2
                className="h3"
                data-tina-field={tinaFieldSafe(inActionSection, "title")}
              >
                {inActionSection?.title}
              </h2>
              <p
                data-tina-field={tinaFieldSafe(inActionSection, "description")}
              >
                {inActionSection?.description}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_4fr] gap-8 lg:gap-12 items-start">
            {includedSection && (
              <div className="">
                {includedSection?.title && (
                  <div className="space-y-2">
                    {/* <h2
                      className="h3"
                      data-tina-field={tinaFieldSafe(includedSection, "title")}
                    >
                      {includedSection.title}
                    </h2> */}
                    {includedSection?.subtitle && (
                      <p
                        className="text-sm text-muted-foreground"
                        data-tina-field={tinaFieldSafe(
                          includedSection,
                          "subtitle"
                        )}
                      >
                        {includedSection.subtitle}
                      </p>
                    )}
                  </div>
                )}
                <div className="space-y-6">
                  {includedSection?.sections?.map(
                    (section: any, sectionIndex: number) => (
                      <div key={sectionIndex} className="space-y-3">
                        <h4
                          className="font-mono text-primary-200 uppercase text-xs border-b border-border pb-1.5"
                          data-tina-field={tinaFieldSafe(section, "title")}
                        >
                          {section.title}
                        </h4>
                        <div className="space-y-2">
                          {section.features?.map(
                            (feature: any, featureIndex: number) => (
                              <div
                                key={featureIndex}
                                className="flex items-start gap-2.5"
                              >
                                <Check className="size-4 stroke-[1.5px] flex-shrink-0 mt-0.5 text-secondary-500" />
                                <span
                                  className="text-base leading-relaxed"
                                  data-tina-field={tinaFieldSafe(
                                    feature,
                                    "name"
                                  )}
                                >
                                  {feature.name}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            {inActionSection?.iframeUrl && (
              <div className="mt-5">
                <Card
                  animateOnHover={false}
                  wrapperClassName="!shadow-none"
                  backgroundVariant="default"
                >
                  <CardContent className="p-0">
                    <div
                      className="relative w-full aspect-video bg-gray-750 overflow-hidden"
                      data-tina-field={tinaFieldSafe(
                        inActionSection,
                        "iframeUrl"
                      )}
                    >
                      <iframe
                        src={inActionSection.iframeUrl}
                        className="absolute top-0 left-0 w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={inActionSection?.title || "In Action"}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </Section>
      )}
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}
